/**
 * Rendering one candidate's invite in the BROWSER, so the bulk-invite modal can
 * show the effect of an edit as it is typed.
 *
 * The server renders the batch once; from then on the recruiter may edit the
 * shared template, and a round trip per keystroke would be both slow and
 * pointless — the values are already known. So the preview response ships every
 * resolved token value per candidate (`vars`) and this module re-renders
 * locally, with the SAME substitution rule as `fmt` in
 * proxy-server/lib/emailTemplates.js: `{token}` is replaced when a value exists,
 * and left verbatim when it does not, so a typo stays visible instead of
 * silently vanishing.
 *
 * What is sent is what this produces — `bulkInviteAction` posts these strings —
 * so the two implementations must not drift. If the server's rule changes, this
 * one changes with it.
 */
import type { FirstInvitePreviewCandidate, InviteVariable } from '../../services/firstInviteApi';
import { isBlockingWarning } from './inviteWarnings';

/** `{token}` → value; unknown tokens are left as-is. Mirrors the server's fmt(). */
export function fmt(template: string, vars: Record<string, string>): string {
  return `${template ?? ''}`.replace(/\{(\w+)\}/g, (m, k: string) =>
    k in vars && vars[k] != null ? String(vars[k]) : m
  );
}

/** The `{tokens}` a template uses, in first-appearance order. */
export function tokensInTemplate(subject: string, body: string): string[] {
  const seen: string[] = [];
  for (const match of `${subject}\n${body}`.matchAll(/\{(\w+)\}/g)) {
    if (!seen.includes(match[1])) seen.push(match[1]);
  }
  return seen;
}

/** Pair each token with what it resolves to for one candidate. */
export function describeVariables(
  tokens: string[],
  vars: Record<string, string>
): InviteVariable[] {
  return tokens.map((token) => ({
    token,
    value: token in vars && vars[token] != null ? String(vars[token]) : '',
    resolved: token in vars && vars[token] != null,
  }));
}

/** Warning codes that depend on the template text, and so must be recomputed. */
const TOKEN_WARNING = /^(unresolved|empty|empty-critical):/;

/** Tokens whose blankness is dangerous, not merely untidy. Mirrors the server. */
const CRITICAL_TOKENS = new Set(['bookingLink', 'candidateFirstName']);

export interface RenderedRow {
  /** The server's row: identity, resolved values, and template-independent warnings. */
  row: FirstInvitePreviewCandidate;
  subject: string;
  body: string;
  variables: InviteVariable[];
  warnings: string[];
  blocked: boolean;
}

/**
 * Render one candidate against the CURRENT template text and re-derive the
 * token warnings from it.
 *
 * Warnings that have nothing to do with the template — no address in Lever, no
 * interview link, no booking URL — are kept from the server verbatim: the
 * recruiter cannot fix those by editing prose, and recomputing them here would
 * mean duplicating the server's knowledge of Lever and the links.
 */
export function renderCandidate(
  row: FirstInvitePreviewCandidate,
  template: { subject: string; body: string }
): RenderedRow {
  const vars = row.vars || {};
  const variables = describeVariables(tokensInTemplate(template.subject, template.body), vars);

  const kept = (row.warnings || []).filter((c) => !TOKEN_WARNING.test(c));
  const unresolved = variables.filter((v) => !v.resolved).map((v) => v.token);
  const blank = variables.filter((v) => v.resolved && !v.value.trim()).map((v) => v.token);
  const criticalBlank = blank.filter((t) => CRITICAL_TOKENS.has(t));
  const softBlank = blank.filter((t) => !CRITICAL_TOKENS.has(t));

  const warnings = [
    ...(unresolved.length ? [`unresolved:${unresolved.join(',')}`] : []),
    ...kept,
    ...(criticalBlank.length ? [`empty-critical:${criticalBlank.join(',')}`] : []),
    ...(softBlank.length ? [`empty:${softBlank.join(',')}`] : []),
  ];

  return {
    row,
    subject: fmt(template.subject, vars),
    body: fmt(template.body, vars),
    variables,
    warnings,
    blocked: warnings.some(isBlockingWarning),
  };
}
